import { useEffect, useMemo, useState } from "react";
import { tryApiGet } from "../api/client";
import { formatNumber, relativeTime } from "../lib/format";
import {
  MC_ALL_REGIONS, countsFor, filterByRegion, nextSort, sortObservers,
  type McObserversPayload, type McSortDir, type McSortKey,
} from "../lib/meshcoreObservers";

// The "MeshCore observers" section of the Health page: every listening station that reports to CoreScope, from
// the stored list GET /api/meshcore/observers (refreshed in the background every few minutes -- this page never
// asks for anything to be calculated).

const COLUMNS: { key: McSortKey; label: string }[] = [
  { key: "name", label: "Observer" },
  { key: "id", label: "Observer ID" },
  { key: "region", label: "Region" },
  { key: "last_heard", label: "Last heard" },
  { key: "rx_1h", label: "Heard, last hour" },
  { key: "rx_24h", label: "Heard, last 24 hours" },
  { key: "rx_7d", label: "Heard, last 7 days" },
];

export function MeshcoreObserversSection() {
  const [data, setData] = useState<McObserversPayload | null | "unavailable">(null);
  const [region, setRegion] = useState<string>(MC_ALL_REGIONS);
  const [sort, setSort] = useState<{ key: McSortKey; dir: McSortDir }>({ key: "last_heard", dir: -1 });

  useEffect(() => {
    let cancelled = false;
    function poll() {
      tryApiGet<McObserversPayload>("/api/meshcore/observers")
        .then((r) => !cancelled && setData(r ?? "unavailable"))
        .catch(() => !cancelled && setData((d) => d ?? "unavailable"));
    }
    poll();
    const id = window.setInterval(poll, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const ready = data && data !== "unavailable" && !data.pending ? data : null;
  const rows = useMemo(() => (ready ? sortObservers(filterByRegion(ready.observers, region), sort.key, sort.dir) : []), [ready, region, sort]);
  const shownCounts = useMemo(() => countsFor(rows), [rows]);
  const filtered = region !== MC_ALL_REGIONS;
  const regionName = ready?.regions.find((r) => r.code === region)?.name ?? "";

  return (
    <section className="page-section">
      <div className="page-section-title">MeshCore observers</div>
      {data === null && <div className="node-detail-note">Loading…</div>}
      {data === "unavailable" && <div className="node-detail-note">Couldn't load the MeshCore observers.</div>}
      {data && data !== "unavailable" && data.pending && (
        <div className="node-detail-note">{data.message ?? "First calculation in progress -- check back in a few minutes."}</div>
      )}
      {ready && (
        <>
          <div className="node-detail-note" style={{ marginBottom: 10 }}>
            {formatNumber(shownCounts.active_1h)} active in the last hour · {formatNumber(shownCounts.seen_24h)} seen in 24 hours ·{" "}
            {formatNumber(shownCounts.seen_7d)} seen in 7 days{filtered ? ` in ${regionName}` : ""}
            {ready.generated_at ? ` · updated ${relativeTime(ready.generated_at)}` : ""}
          </div>
          <div className="node-list-toolbar audit-filter-row">
            <span className="audit-filter">
              <label htmlFor="mc-observers-region">Region</label>
              <select id="mc-observers-region" value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value={MC_ALL_REGIONS}>All regions ({formatNumber(ready.counts.seen_7d)})</option>
                {ready.regions.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.name} ({formatNumber(r.count)})
                  </option>
                ))}
              </select>
            </span>
          </div>
          {rows.length === 0 ? (
            <div className="empty-state">{filtered ? "No observers seen in this region in the last 7 days." : "No MeshCore observers seen in the last 7 days."}</div>
          ) : (
            <div className="node-list-table-wrap">
              <table className="node-list-table">
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        onClick={() => setSort((s) => nextSort(s, c.key))}
                        aria-sort={sort.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}
                        style={{ cursor: "pointer" }}
                      >
                        {c.label}
                        {sort.key === c.key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
                      </th>
                    ))}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <tr key={o.id}>
                      <td>{o.name}</td>
                      <td style={{ fontFamily: "monospace" }}>{o.id}</td>
                      <td>{o.region_name}</td>
                      <td>{o.last_heard ? relativeTime(o.last_heard) : "never"}</td>
                      <td>{formatNumber(o.rx_1h)}</td>
                      <td>{formatNumber(o.rx_24h)}</td>
                      <td>{formatNumber(o.rx_7d)}</td>
                      <td>{o.active ? "Active" : "Quiet"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="node-detail-note" style={{ marginTop: 8 }}>
            A region comes from the position of the node with the same ID; with no matching node or no position it shows as Unknown
            location. Counts are rounded to 10-minute steps. The list is refreshed every few minutes.
          </div>
        </>
      )}
    </section>
  );
}
